// Single-API provider workspace page (/workspace/:apiId/:section[/:subsection]).
//
// 版本生命周期重构：左上角「版本切换器」选当前编辑的大版本（V1/V2…），编辑段读到的
// api 即按所选大版本作用域加载（工作副本优先，否则 serving 修订）。保存/发布都落到所选
// 大版本。版本的新建/默认/回滚/历史在「管理版本…」弹窗里，不再占一个 tab。

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { XShell } from '../shell.jsx';
import { XPrim } from '../primitives.jsx';
import { XApi } from '../services.js';
import { WorkspaceShell } from '../ui/workspace-shell.jsx';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../ui/index.js';
import { TestsSection } from './workspace-sections/tests-section.jsx';
import { SettingsSection } from './workspace-sections/settings-section.jsx';
import { BasicInfoForm } from './workspace-sections/basic-form.jsx';
import { EndpointsManager } from './workspace-sections/endpoints-manager.jsx';
import { VersionManagerDialog } from './workspace-sections/version-panel.jsx';
import { ReviewReportDialog } from './workspace-sections/review-report.jsx';
import { TrafficSection } from './workspace-sections/analytics-traffic.jsx';
import { UsersSection } from './workspace-sections/analytics-users.jsx';
import { RevenueSection } from './workspace-sections/analytics-revenue.jsx';
import { AnomaliesSection } from './workspace-sections/anomalies-section.jsx';
import { SkillSection } from './workspace-sections/skill-section.jsx';

const SECTIONS = ['basic', 'apis', 'skill', 'tests', 'analytics', 'anomalies', 'settings'];

// 顶部审核状态条（所选大版本工作修订）。审核不阻塞 provider：只在「测试进行中」与「已驳回」
// 两种需要 provider 关注的状态显眼提示；通过/已上线（含进抽查的）不挂阻塞条。
const REVIEW_BANNER = {
  reviewing: { fg: '#2563eb', bg: 'rgba(59,130,246,0.10)', label: '审核中', text: 'AI 正在自主测试端点…通过后自动上线，线上不受影响。', btn: '查看进度' },
  rejected: { fg: '#dc2626', bg: 'rgba(239,68,68,0.10)', label: '已驳回', text: '自动审核未通过，已驳回到沙箱。修复后可重新提交（可多次提交）；线上旧版本继续在跑。', btn: '查看失败原因' },
};

// 某大版本当前「可编辑/展示」的版本号：工作副本优先（沙箱里在改的那条），否则 serving。
const displaySlug = (m) => {
  if (!m) return null;
  if (m.working) return `v${m.major}.${m.working.revision}`;
  const serving = (m.history || []).find((h) => h.isServing);
  return serving ? `v${m.major}.${serving.revision}` : null;
};

export const WorkspaceApiPage = () => {
  const { route, navigate } = XShell.useRoute();
  const apiId = route.params.apiId;
  // The 4-segment route /workspace/:apiId/analytics/:subsection has literal
  // "analytics" in the path — captured into route.params.subsection but NOT
  // route.params.section. So if subsection exists, force section='analytics'.
  const subsection = route.params.subsection;
  const rawSection = subsection ? 'analytics' : (route.params.section || 'basic');
  const section = SECTIONS.includes(rawSection) ? rawSection : 'basic';

  const [api, setApi] = useState(null);        // 按 selectedMajor 作用域加载的 api（编辑段消费）
  const [overview, setOverview] = useState(null); // versionOverview（切换器 / 管理弹窗 / 状态徽标）
  const [selectedMajor, setSelectedMajor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [managerOpen, setManagerOpen] = useState(false);

  // 把某大版本作用域的 api 装进表单。失败（版本号对不上等）回退到默认 get。
  const loadScopedApi = useCallback(async (major, ov) => {
    const m = ov?.majors?.find((x) => x.major === major);
    const slug = displaySlug(m);
    try {
      setApi(await XApi.apiSvc.get(apiId, slug ? { version: slug } : {}));
    } catch {
      const fallback = await XApi.apiSvc.get(apiId).catch(() => null);
      if (fallback) setApi(fallback);
    }
  }, [apiId]);

  // 初次加载：overview → 选默认大版本 → 装作用域 api。overview 拿不到则退化为普通 get。
  useEffect(() => {
    if (!apiId) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    (async () => {
      try {
        const ov = await XApi.apiSvc.versionOverview(apiId).catch(() => null);
        if (cancelled) return;
        if (ov && ov.majors?.length) {
          setOverview(ov);
          setSelectedMajor(ov.currentMajor);
          await loadScopedApi(ov.currentMajor, ov);
        } else {
          const data = await XApi.apiSvc.get(apiId);
          if (!cancelled) setApi(data);
        }
      } catch (e) {
        if (!cancelled) setErr(e?.message || 'Failed to load API');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiId, loadScopedApi]);

  // 刷新 overview + 当前大版本的作用域 api（保存/发布/版本管理动作后调用）。
  const refreshAll = useCallback(async () => {
    const ov = await XApi.apiSvc.versionOverview(apiId).catch(() => null);
    if (!ov) {
      const data = await XApi.apiSvc.get(apiId).catch(() => null);
      if (data) setApi(data);
      return;
    }
    setOverview(ov);
    const major = ov.majors.some((m) => m.major === selectedMajor)
      ? selectedMajor
      : ov.currentMajor;
    setSelectedMajor(major);
    await loadScopedApi(major, ov);
  }, [apiId, selectedMajor, loadScopedApi]);

  // ── IN_REVIEW 轮询 + 出结果 toast ──
  // 有工作修订正在自动审核(reviewing)时定时 refreshAll；审核解析时按「那条修订的真实结果」弹 toast。
  const { toast } = XPrim.useToast();
  const reviewingRef = useRef(new Map()); // revId -> major（上一轮在审的修订）

  useEffect(() => {
    if (!overview) return;
    // 按 revId 索引当前所有修订（working + history）的 outcome
    const cur = new Map(); // revId -> { major, outcome, isReviewing }
    for (const m of overview.majors || []) {
      if (m.working) cur.set(m.working.id, { major: m.major, outcome: m.working.review?.outcome ?? null, isReviewing: m.working.review?.outcome === 'reviewing' });
      for (const h of m.history || []) cur.set(h.id, { major: m.major, outcome: h.review?.outcome ?? null, isReviewing: false });
    }
    // 上一轮在审的修订 → 这轮解析了，按它的真实 outcome 弹（修订消失=被放弃，不弹，避免误报）。
    // 审核不阻塞：passed / passed_pending 对 provider 都是「已上线」（后者另进管理员抽查，不打扰 provider）。
    for (const [revId, major] of reviewingRef.current) {
      const c = cur.get(revId);
      if (!c || c.isReviewing) continue;
      if (c.outcome === 'passed' || c.outcome === 'passed_pending') toast({ title: `v${major} 自动审核通过，已上线`, type: 'success' });
      else if (c.outcome === 'rejected') toast({ title: `v${major} 自动审核未通过，已驳回 —— 见上方状态条「查看失败原因」`, type: 'error' });
    }
    const nowReviewing = new Map();
    for (const [revId, info] of cur) if (info.isReviewing) nowReviewing.set(revId, info.major);
    reviewingRef.current = nowReviewing;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview]);

  useEffect(() => {
    const anyReviewing = (overview?.majors || []).some((m) => m.working?.review?.outcome === 'reviewing');
    if (!anyReviewing) return;
    const t = setInterval(() => { refreshAll(); }, 8000);
    return () => clearInterval(t);
  }, [overview, refreshAll]);

  // 所选大版本工作修订的审核态：驱动状态条 + 编辑锁 + 发布按钮态。
  const selMajorObj = (overview?.majors || []).find((m) => m.major === selectedMajor) || null;
  const workingReviewOutcome = selMajorObj?.working?.review?.outcome || null;
  // 只有「测试进行中」与「已驳回」需要 provider 关注 → 挂状态条；通过/已上线不挂。
  const bannerOutcome = ['reviewing', 'rejected'].includes(workingReviewOutcome) ? workingReviewOutcome : null;
  // 编辑锁只在 reviewing（AI 正测，改了会和在测的副本打架）。审核不阻塞：其它态都可编辑。
  const inReview = workingReviewOutcome === 'reviewing';
  // 状态条可手动关闭：按「api+大版本+态+工作修订」做 key 持久化——关掉此次后不再弹，
  // 但新一次驳回/新修订（key 变）会重新出现。
  const bannerKey = bannerOutcome
    ? `${api?.id || ''}:${selectedMajor}:${bannerOutcome}:${selMajorObj?.working?.id || ''}`
    : null;
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return localStorage.getItem('xapi.reviewBanner.dismissed'); } catch { return null; }
  });
  const showBanner = !!bannerOutcome && bannerDismissed !== bannerKey;
  const dismissBanner = () => {
    setBannerDismissed(bannerKey);
    try { localStorage.setItem('xapi.reviewBanner.dismissed', bannerKey); } catch {}
  };
  const [reviewDlg, setReviewDlg] = useState(null); // { revId, label }

  // 未保存守卫:basic / apis 段把 { isDirty, hasErrors, save } 上报到这里。切段或切大版本
  // 且有未保存改动时,弹「保存并离开 / 放弃并离开 / 取消」。
  const guardRef = React.useRef(null);
  const onDirty = React.useCallback((g) => { guardRef.current = g; }, []);
  const [pending, setPending] = useState(null); // { type:'nav', key, sub } | { type:'major', major }
  const [savingLeave, setSavingLeave] = useState(false);

  const doNavigate = (key, sub) => {
    navigate('workspace-api', sub ? { apiId, section: key, subsection: sub } : { apiId, section: key });
  };
  const switchMajor = async (major) => {
    setSelectedMajor(major);
    await loadScopedApi(major, overview);
  };
  const applyPending = (p) => {
    guardRef.current = null;
    if (p.type === 'nav') doNavigate(p.key, p.sub);
    else if (p.type === 'major') switchMajor(p.major);
  };

  const onSelect = (key, sub) => {
    if (guardRef.current?.isDirty && key !== section) { setPending({ type: 'nav', key, sub }); return; }
    doNavigate(key, sub);
  };
  const onSelectMajor = (major) => {
    if (major === selectedMajor) return;
    if (guardRef.current?.isDirty) { setPending({ type: 'major', major }); return; }
    switchMajor(major);
  };

  // 保存并离开:调用当前段的 save();成功才执行挂起动作,失败留在原地。
  const saveAndLeave = async () => {
    const g = guardRef.current;
    if (!g?.save) return;
    setSavingLeave(true);
    try {
      const ok = await g.save();
      if (ok) { const p = pending; setPending(null); applyPending(p); }
    } finally {
      setSavingLeave(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 32, color: 'var(--fg-2)' }}>Loading workspace…</div>;
  }
  if (err || !api) {
    return (
      <div style={{ maxWidth: 480, margin: '120px auto', padding: 32, textAlign: 'center', color: 'var(--fg-2)' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
        <h2 style={{ margin: '0 0 8px', fontSize: 18, color: 'var(--fg-0)' }}>未找到该 API</h2>
        <p style={{ fontSize: 13, color: 'var(--fg-3)', marginTop: 8, lineHeight: 1.6 }}>
          这个 API 可能不存在，或者不在你的项目下。
        </p>
        <button type="button" onClick={() => navigate('workspace')} style={{ marginTop: 24, padding: '8px 18px', background: 'var(--fg-0)', color: 'var(--bg-0)', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>← 返回 My APIs</button>
        {err && (
          <details style={{ marginTop: 24, fontSize: 11, color: 'var(--fg-4)', textAlign: 'left' }}>
            <summary style={{ cursor: 'pointer' }}>technical details</summary>
            <pre style={{ marginTop: 8, padding: 10, background: 'var(--bg-1)', borderRadius: 4, overflowX: 'auto' }}>{err}</pre>
          </details>
        )}
      </div>
    );
  }

  return (
    <>
      <WorkspaceShell
        api={api}
        activeSection={section}
        activeSubsection={subsection}
        onSelect={onSelect}
        versionOverview={overview}
        selectedMajor={selectedMajor}
        onSelectMajor={onSelectMajor}
        onOpenVersionManager={() => setManagerOpen(true)}
      >
        {/* 审核状态条：所选大版本工作修订「测试进行中 / 已驳回」时常驻顶部，直达报告。 */}
        {showBanner && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 32px', background: REVIEW_BANNER[bannerOutcome].bg, borderBottom: `1px solid ${REVIEW_BANNER[bannerOutcome].fg}33`, fontSize: 12.5, color: REVIEW_BANNER[bannerOutcome].fg, flexWrap: 'wrap' }}>
            {bannerOutcome === 'reviewing' && <span className="ws-spin" style={{ width: 11, height: 11, borderRadius: '50%', border: `2px solid ${REVIEW_BANNER[bannerOutcome].fg}`, borderTopColor: 'transparent', display: 'inline-block' }} />}
            <span style={{ fontWeight: 700 }}>v{selectedMajor} · {REVIEW_BANNER[bannerOutcome].label}</span>
            <span style={{ color: 'var(--fg-2)' }}>{REVIEW_BANNER[bannerOutcome].text}</span>
            <div style={{ flex: 1 }} />
            <button type="button" className="btn btn-sm" onClick={() => setReviewDlg({ revId: selMajorObj.working.id, label: `v${selectedMajor} 工作副本` })}>{REVIEW_BANNER[bannerOutcome].btn}</button>
            <button
              type="button"
              className="btn btn-sm btn-ghost btn-icon"
              title="关闭此提示（修复重提或新驳回会再次出现）"
              aria-label="关闭"
              onClick={dismissBanner}
              style={{ color: REVIEW_BANNER[bannerOutcome].fg, flexShrink: 0 }}
            >
              ✕
            </button>
          </div>
        )}
        <SectionContent
          section={section}
          subsection={subsection}
          api={api}
          selectedMajor={selectedMajor}
          inReview={inReview}
          onApiRefreshed={refreshAll}
          onDirty={onDirty}
        />
      </WorkspaceShell>

      <VersionManagerDialog
        open={managerOpen}
        api={api}
        selectedMajor={selectedMajor}
        onClose={() => setManagerOpen(false)}
        onSelectMajor={onSelectMajor}
        onApiRefreshed={refreshAll}
      />

      <ReviewReportDialog
        open={!!reviewDlg}
        apiId={api?.id}
        revId={reviewDlg?.revId}
        revLabel={reviewDlg?.label}
        onClose={() => setReviewDlg(null)}
      />

      {pending && (
        <Dialog open onClose={() => (savingLeave ? null : setPending(null))} maxWidth={440}>
          <DialogHeader title="有未保存的改动" onClose={() => (savingLeave ? null : setPending(null))} />
          <DialogBody>
            <div style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--fg-2)' }}>
              当前页面有未保存的改动。你可以先保存再{pending.type === 'major' ? '切换版本' : '离开'},或放弃这些改动。
              {guardRef.current?.hasErrors && (
                <div style={{ marginTop: 8, color: 'var(--err)', fontSize: 12.5 }}>
                  有字段需要修正,无法保存 —— 请先「取消」回去修正,或放弃改动。
                </div>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <button type="button" className="btn btn-sm" disabled={savingLeave} onClick={() => setPending(null)}>取消</button>
            <button type="button" className="btn btn-sm" disabled={savingLeave} style={{ color: 'var(--err)', borderColor: 'var(--err)' }} onClick={() => { const p = pending; setPending(null); applyPending(p); }}>放弃并{pending.type === 'major' ? '切换' : '离开'}</button>
            <button type="button" className="btn btn-sm btn-primary" disabled={savingLeave || guardRef.current?.hasErrors} title={guardRef.current?.hasErrors ? '有字段需要修正,无法保存' : undefined} onClick={saveAndLeave}>
              {savingLeave ? '保存中…' : `保存并${pending.type === 'major' ? '切换' : '离开'}`}
            </button>
          </DialogFooter>
        </Dialog>
      )}
    </>
  );
};

// 各段内容由 workspace-sections/* 单独承载；这里只做路由分发。
const SectionContent = ({ section, subsection, api, selectedMajor, inReview, onApiRefreshed, onDirty }) => {
  switch (section) {
    case 'basic':
      return <BasicInfoForm api={api} selectedMajor={selectedMajor} inReview={inReview} onApiRefreshed={onApiRefreshed} onDirtyChange={onDirty} />;
    case 'apis':
      return <EndpointsManager api={api} selectedMajor={selectedMajor} inReview={inReview} onApiRefreshed={onApiRefreshed} onDirtyChange={onDirty} />;
    case 'skill':
      return <SkillSection api={api} onApiRefreshed={onApiRefreshed} />;
    case 'tests':
      return <TestsSection api={api} />;
    case 'analytics': {
      const sub = subsection || 'traffic';
      if (sub === 'users') return <UsersSection api={api} />;
      if (sub === 'revenue') return <RevenueSection api={api} />;
      return <TrafficSection api={api} />;
    }
    case 'anomalies':
      return <AnomaliesSection api={api} />;
    case 'settings':
      return <SettingsSection api={api} onApiRefreshed={onApiRefreshed} />;
    default:
      return null;
  }
};
